import {
  useEffect,
  useState,
  createContext,
  useContext,
  ReactNode,
  useRef,
} from "react";
import { useClientNotes } from "@/hooks/use-client-notes";
import { getData, setData, deleteData } from "@/lib/indexed-db";
import { useSync } from "@/hooks/use-sync";
import { useAuth } from "@/providers/auth-provider";
import { dateTimeFormat } from "@/lib/date-tools";

export interface Note {
  _id: string;
  title: string;
  description: string;
  content: string;
  parent?: string;
  children?: string[];
  createdAt: string;
  updatedAt: string;
  needsSync?: boolean;
  needsCreate?: boolean;
}

const NoteProviderContext = createContext<any>(undefined);

export function NoteProvider({ children, ...props }: { children: ReactNode }) {
  const {
    notes: allNotes,
    loading,
    error,
    createNote: createNoteAPI,
    updateNote: updateNoteAPI,
    deleteNote: deleteNoteAPI,
  } = useClientNotes();
  const { user } = useAuth();
  const [notes, setNotes] = useState<Note[]>([]);
  const [rootNotes, setRootNotes] = useState<Note[]>([]);
  const [activeNote, setActiveNote] = useState<Note | null>(null);
  const [activeParent, setActiveParent] = useState<Note | null>(null);
  const activeNoteRef = useRef<Note | null>(null);
  const activeParentRef = useRef<Note | null>(null);
  const initialized = useRef(false);

  //TODO: 빠른 로딩과 오프라인 지원을 위해 노트 목록 전체를 Indexed DB에 저장하여 사용하고, 주기적으로 서버와 동기화한다.
  //TODO: 노트 목록을 가져올 때, Indexed DB에 저장된 노트 목록을 먼저 가져오고, 서버에서 가져온 노트 목록과 비교하여 업데이트한다.

  useEffect(() => {
    const init = async () => {
      const result = await getData("notes", user._id);
      setNotes(result?.data || []);
      initialized.current = true;
      console.log("[Note Provider] Init Notes: ", user.name, result);
    };
    if (user?._id) {
      init();
    }
  }, [user?._id]);

  useEffect(() => {
    if (!loading) {
      if (!error) {
        setNotes(allNotes);
        console.log("[Note Provider] Fetch Notes: ", allNotes);
      } else {
        console.error("[Note Provider] Fetch Notes Error: ", error);
      }
    }
  }, [loading, error]);

  useEffect(() => {
    if (!initialized.current) return;
    if (notes) {
      const updatedRootNotes = notes
        .filter((note: Note) => !note.parent)
        .sort((a, b) => a.title.localeCompare(b.title));
      setRootNotes(updatedRootNotes);
      setData("notes", user._id, notes);
      console.log("[Note Provider] Update Notes: ", notes);
    }
  }, [notes]);

  useEffect(() => {
    activeNoteRef.current = activeNote;
    activeParentRef.current = activeParent;
    console.log("[Note Provider] ActiveNote: ", activeNote);
  }, [activeNote, activeParent]);

  const updateActiveNote = (note: Note) => {
    // console.log("[Note Provider] UpdateActiveNote: ", note);
    if (activeNoteRef.current?._id === note._id) {
      setActiveNote(note);
    }
    if (activeParentRef.current?._id === note._id) {
      setActiveParent(note);
    }
  };

  const createNewNote = async (parentId?: string) => {
    const newNote: Note = {
      _id: Date.now().toString(),
      title: dateTimeFormat(Date.now().toString()), //getTempNoteTitle(),
      description: "",
      content: "",
      parent: parentId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      needsCreate: true,
    };
    // return newNote;
    selectNote(newNote);
    return await createNote(newNote);
  };

  const createNote = async (note: Note) => {
    if (note && note.title) {
      // 노트 생성.
      const createdNote = await createNoteAPI(note);
      console.log("[Note Provider] CreateNote: ", createdNote);
      let prevNotes = [...notes];

      // 부모 노트의 children에 추가
      if (createdNote.parent) {
        const parent = notes.find((n) => n._id === createdNote.parent);
        if (parent) {
          // Apollo Client는 성능과 데이터 일관성을 위해 받아온 데이터를 불변(immutable) 객체로 캐시에 저장합니다.(읽기전용)
          // 이는 의도치 않은 데이터 변경을 방지하기 위한 것입니다.
          // 그러므로 Apollo Client에서는 배열을 변경할 때는 새로운 배열을 생성해야 합니다.
          // parent.children = parent.children || [];
          // parent.children.push(newNote._id);

          // 새로운 배열을 만들어서 통째로 업데이트
          const updatedChildren = [...(parent.children || []), createdNote._id];
          const updatedParent = { ...parent, children: updatedChildren };
          prevNotes = prevNotes.map((n: Note) =>
            n._id === parent._id ? updatedParent : n,
          );
          updateActiveNote(updatedParent);
        }
      }

      // 새로운 노트를 추가
      setNotes([createdNote, ...prevNotes]);
      // updateActiveNote(newNote);

      if (activeNoteRef.current?._id === note._id) {
        selectNote(createdNote);
      }
    } else {
      console.error("[Note Provider] CreateNote: param is missing, ", note);
    }
  };

  const updateNote = async (note: Note, updates: Partial<Note>) => {
    if (note && updates) {
      // 노트 업데이트.
      const tempUpdate = { ...note, ...updates };
      setNotes(
        notes.map((n: Note) => (n._id === tempUpdate._id ? tempUpdate : n)),
      );
      updateActiveNote(tempUpdate);
      // 서버에 업데이트.
      const updatedNote = await updateNoteAPI(note._id, updates);
      console.log("[Note Provider] UpdateNote: ", updatedNote);
      setNotes(
        notes.map((n: Note) => (n._id === updatedNote._id ? updatedNote : n)),
      );
      updateActiveNote(updatedNote);
    } else {
      console.error(
        "[Note Provider] UpdateNote: param is missing, ",
        note,
        updates,
      );
    }
  };

  const deleteNote = async (note: Note) => {
    if (activeNoteRef.current?._id === note._id) {
      selectNote(null);
    }
    if (activeParentRef.current?._id === note._id) {
      selectParent(null);
    }
    await deleteNoteAPI(note._id);

    // 부모 노트의 children에서 제거
    let prevNotes = [...notes];
    if (note.parent) {
      const parent = notes.find((n) => n._id === note.parent);
      if (parent) {
        const updatedChildren = parent.children?.filter(
          (childId: string) => childId !== note._id,
        );
        const updatedParent = { ...parent, children: updatedChildren };
        prevNotes = notes.map((n: Note) =>
          n._id === updatedParent._id ? updatedParent : n,
        );
        updateActiveNote(updatedParent);
      }
    }

    setNotes(prevNotes.filter((n: Note) => n._id !== note._id));
  };

  const selectNote = (param: string | Note | null) => {
    if (!param) {
      return setActiveNote(null);
    }

    if (typeof param === "string") {
      const foundNote = notes.find((note) => note._id === param);
      setActiveNote(foundNote ?? null);
    } else {
      setActiveNote(param);
    }
  };

  const selectParent = (param: string | Note | null) => {
    if (!param) {
      return setActiveParent(null);
    }

    const foundNote =
      typeof param === "string"
        ? notes.find((note) => note._id === param)
        : param;
    if (foundNote) {
      // 이미 선택한 부모라면, 무시
      if (foundNote._id === activeParentRef.current?._id) {
        return;
      }
      setActiveParent(foundNote);
    }
  };

  const moveToParent = (child: Note) => {
    if (!child) {
      return;
    }

    const parent = notes.find((note) => note._id === child.parent);
    if (parent) {
      selectParent(parent);
    } else {
      selectParent(null);
    }
  };

  //========================================================================
  // Sync
  //========================================================================
  const syncNote = async () => {
    // for update.
    for (const note of notes) {
      const draft = await getData("notes", note._id);
      if (draft) {
        const { data } = draft;
        await updateNote(note, {
          title: data.title,
          description: data.description,
          content: data.content,
        });
        deleteData("notes", note._id);
        // console.log("[Note Provider] SyncNote: updated, ", data);
      }
    }
  };

  const { isSyncing, syncNow } = useSync(syncNote, 10 * 1000);

  const value = {
    notes,
    rootNotes,
    activeNote,
    selectNote,
    activeParent,
    selectParent,
    moveToParent,
    createNewNote,
    // createNote,
    updateNote,
    deleteNote,
    loading,
    // syncNote,
    isSyncing,
    syncNow,
  };

  return (
    <NoteProviderContext.Provider {...props} value={value}>
      {children}
    </NoteProviderContext.Provider>
  );
}

export const useNotes = () => {
  const context = useContext(NoteProviderContext);
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
};
